import { Injectable } from '@angular/core';
import { Platform } from '@ionic/angular';
import { Subject } from 'rxjs';

declare let window: any;

export interface DeepLinkMatch {
  $route: any;
  $args: any;
  $link: any;
}

/**
 * Service for handling deep links in the application.
 * Supports both custom URL schemes and universal links.
 */
@Injectable({
  providedIn: 'root',
})
export class DeepLinksService {
  private deepLinkSubject = new Subject<DeepLinkMatch>();

  constructor(private platform: Platform) {}

  /**
   * Returns an observable for deep link events.
   */
  public onDeepLink() {
    return this.deepLinkSubject.asObservable();
  }

  /**
   * Initializes deep link handling for the application.
   * Only works on Cordova platform.
   */
  public initializeDeepLinks(): void {
    if (!this.platform.is('cordova')) {
      return;
    }

    this.platform.ready().then(() => {
      this.setupDeepLinkListener();
      this.checkForInitialDeepLink();
    });
  }

  /**
   * Checks for initial deep link data when the app starts.
   * Looks for deep link data in various window properties and plugins.
   */
  private checkForInitialDeepLink(): void {
    if ((window as any).DeepLinkData) {
      this.handleDeepLink({ $link: (window as any).DeepLinkData });
    }

    if ((window as any).cordova && (window as any).cordova.startupUrl) {
      this.handleDeepLink({ $link: (window as any).cordova.startupUrl });
    }

    if (window.plugins && window.plugins.universalLinks) {
      if (window.plugins.universalLinks.getInitialLink) {
        window.plugins.universalLinks.getInitialLink((data: any) => {
          if (data) {
            this.handleDeepLink({ $link: data });
          }
        });
      }
    }
  }

  /**
   * Sets up listeners for both custom schemes and universal links.
   * Attempts multiple fallback methods to ensure deep link handling works.
   */
  private setupDeepLinkListener(): void {
    let hasCustomScheme = false;
    let hasUniversalLinks = false;

    if (window.plugins && window.plugins.universalLinks) {
      this.setupUniversalLinks();
      hasUniversalLinks = true;
    }

    if (window.IonicDeeplink) {
      this.setupIonicDeeplink();
      hasCustomScheme = true;
    } else if (window.deeplinks) {
      this.setupAlternativeDeeplinks();
      hasCustomScheme = true;
    } else if (window.plugins && window.plugins.deeplinks) {
      this.setupPluginsDeeplinks();
      hasCustomScheme = true;
    }

    this.setupIntentDataFallback();
    if (!hasCustomScheme && !hasUniversalLinks) {
      document.addEventListener('deviceready', () => {
        setTimeout(() => {
          let hasCustomSchemeDelayed = false;
          let hasUniversalLinksDelayed = false;
          if (window.IonicDeeplink) {
            this.setupIonicDeeplink();
            hasCustomSchemeDelayed = true;
          } else if (window.deeplinks) {
            this.setupAlternativeDeeplinks();
            hasCustomSchemeDelayed = true;
          } else if (window.plugins && window.plugins.deeplinks) {
            this.setupPluginsDeeplinks();
            hasCustomSchemeDelayed = true;
          }
          if (window.plugins && window.plugins.universalLinks && !hasUniversalLinks) {
            this.setupUniversalLinks();
            hasUniversalLinksDelayed = true;
          }
        }, 1000);
      });
    }
  }

  /**
   * Sets up IonicDeeplink plugin for handling custom URL schemes.
   */
  private setupIonicDeeplink(): void {
    try {
      window.IonicDeeplink.route(
        {
          '*': {},
        },
        (match: any) => {
          this.handleDeepLink(match);
        },
        (nomatch: any) => {
          if (nomatch && nomatch.$link) {
            this.handleDeepLink(nomatch);
          }
        },
      );
    } catch (error) {}
  }

  /**
   * Sets up alternative deeplinks plugin for handling custom URL schemes.
   */
  private setupAlternativeDeeplinks(): void {
    try {
      window.deeplinks.route(
        {
          '*': {},
        },
        (match: any) => {
          this.handleDeepLink(match);
        },
        (nomatch: any) => {
          if (nomatch && nomatch.$link) {
            this.handleDeepLink(nomatch);
          }
        },
      );
    } catch (error) {}
  }

  /**
   * Sets up plugins.deeplinks for handling custom URL schemes.
   */
  private setupPluginsDeeplinks(): void {
    try {
      window.plugins.deeplinks.route(
        {
          '*': {},
        },
        (match: any) => {
          this.handleDeepLink(match);
        },
        (nomatch: any) => {
          if (nomatch && nomatch.$link) {
            this.handleDeepLink(nomatch);
          }
        },
      );
    } catch (error) {}
  }

  /**
   * Sets up universal links handling for iOS and Android.
   * Subscribes to multiple events to ensure foreground handling works.
   */
  private setupUniversalLinks(): void {
    if (window.plugins.universalLinks.subscribe) {
      window.plugins.universalLinks.subscribe(null, (data: any) => {
        this.handleDeepLink({ $link: data });
      });
      window.plugins.universalLinks.subscribe('deepLink', (data: any) => {
        this.handleDeepLink({ $link: data });
      });
      window.plugins.universalLinks.subscribe(null, (data: any) => {
        this.handleDeepLink({ $link: data });
      });
    } else if (window.plugins.universalLinks.onDeepLink) {
      window.plugins.universalLinks.onDeepLink((data: any) => {
        this.handleDeepLink({ $link: data });
      });
    }
  }

  /**
   * Processes deep link matches and extracts URL information.
   * Creates a standardized DeepLinkMatch object and emits it through the subject.
   */
  private handleDeepLink(match: any): void {
    if (!match) {
      return;
    }

    let url = '';

    if (match.$link && match.$link.url) {
      url = match.$link.url;
    } else if (match.$link && match.$link.path) {
      url = match.$link.path;
    } else if (match.$link && typeof match.$link === 'string') {
      url = match.$link;
    } else if (match.url) {
      url = match.url;
    }

    if (url) {
      const finalUrl = this.extractPathFromUrl(url);

      const deepLinkMatch: DeepLinkMatch = {
        $route: match.$route || {},
        $args: match.$args || {},
        $link: match.$link || { url: finalUrl },
      };

      (deepLinkMatch as any).url = finalUrl;

      this.deepLinkSubject.next(deepLinkMatch);
    }
  }

  /**
   * Extracts the path from various URL formats.
   * Handles custom schemes, universal links, and standard URLs.
   */
  private extractPathFromUrl(url: string): string {
    try {
      if (url.startsWith('racional://')) {
        const path = url.replace('racional://', '/');
        return path;
      }

      if (url.startsWith('https://app.racional.cl')) {
        const urlObj = new URL(url);
        return urlObj.pathname;
      }

      if (url.startsWith('/')) {
        return url;
      }

      const parsedUrl = new URL(url);
      return parsedUrl.pathname;
    } catch (error) {
      return url;
    }
  }

  /**
   * Sets up fallback methods for handling intent data and custom schemes.
   * Includes WebIntent plugin support and global URL handling.
   */
  private setupIntentDataFallback(): void {
    document.addEventListener(
      'deviceready',
      () => {
        this.setupWebIntentListener();
      },
      false,
    );

    setTimeout(() => {
      this.setupWebIntentListener();
    }, 1000);

    (window as any).handleOpenURL = (incomingUrl: string) => {
      try {
        if (incomingUrl && incomingUrl.startsWith('racional://')) {
          this.handleDeepLink({ $link: incomingUrl });
        }
      } catch (error) {}
    };

    document.addEventListener(
      'resume',
      () => {
        this.checkForIntentData();
      },
      false,
    );

    setTimeout(() => {
      this.checkForIntentData();
    }, 2000);
  }

  /**
   * Sets up WebIntent plugin for handling Android intents.
   * Listens for both initial intents and new intents when the app is running.
   */
  private setupWebIntentListener(): void {
    if ((window as any).plugins && (window as any).plugins.webintent) {
      (window as any).plugins.webintent.getUri((uri: string) => {
        if (uri && uri.startsWith('racional://')) {
          this.handleDeepLink({ $link: uri });
        }
      });

      (window as any).plugins.webintent.onNewIntent((intent: any) => {
        let incomingUrl = '';
        if (typeof intent === 'string') {
          incomingUrl = intent;
        } else if (intent && intent.data) {
          incomingUrl = intent.data;
        }
        if (incomingUrl.startsWith('racional://')) {
          this.handleDeepLink({ $link: incomingUrl });
        }
      });
    }
  }

  /**
   * Checks various sources for intent data that might contain deep link information.
   * Searches through multiple possible locations where intent data could be stored.
   */
  private checkForIntentData(): void {
    const possibleSources = [
      'window.intent',
      'window.plugins.intent',
      'window.plugins.webintent',
      'cordova.plugins.intent',
    ];

    possibleSources.forEach((source) => {
      try {
        const intentData = this.getNestedProperty(window, source);
        if (intentData) {
          if (intentData.data || intentData.action) {
            this.handleIntentData(intentData);
          }
        }
      } catch (error) {
        // Silently continue - this is expected for most sources
      }
    });

    if ((window as any).cordova && (window as any).cordova.startupUrl) {
      this.handleDeepLink({ $link: (window as any).cordova.startupUrl });
    }
  }

  /**
   * Safely retrieves nested properties from an object using a dot-notation path.
   */
  private getNestedProperty(obj: any, path: string): any {
    return path.split('.').reduce((current, prop) => current && current[prop], obj);
  }

  /**
   * Processes intent data to extract URL information.
   * Handles various Android intent formats and extracts URLs from different locations.
   */
  private handleIntentData(intentData: any): void {
    let url = '';

    if (intentData.data) {
      url = intentData.data;
    } else if (intentData.action === 'android.intent.action.VIEW' && intentData.extras) {
      url = intentData.extras.android_intent_data || intentData.extras.data || '';
    }

    if (url && url.startsWith('racional://')) {
      this.handleDeepLink({ $link: url });
    }
  }
}
